// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm, type FormProps } from 'redux-form';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import type { Dispatch } from 'state/types';
import DialogPanel from 'view/App/DialogPanels/DialogPanel';
import { hideDialogPanel } from 'state/commands/dialogPanelCommands';
import localizedStrings from 'util/localizedStrings';
import { CREATE_CERT_REQUEST_PANEL_ID } from 'util/constants';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import { getActivePanelId } from 'state/selectors';
import LabeledField from 'view/Forms/LabeledField';
import { createCertRequest } from 'state/commands/certCommands';
import { normalizeSANs } from 'util/certUtils';

type Props = {
  activePanelId: ?string,
  hideDialogPanel: typeof hideDialogPanel,
  intl: IntlShape,
} & FormProps;

const fields = [
  {
    label: localizedStrings.FULLY_QUALIFIED_DOMAIN_NAME,
    dataTestName: 'cert-create-request-fqdn',
    id: 'FQDN',
    isRequired: true,
  },
  {
    label: localizedStrings.ORGANIZATION_NAME,
    dataTestName: 'cert-create-request-org-name',
    id: 'organizationName',
    isRequired: true,
  },
  {
    label: localizedStrings.ORGANIZATIONAL_UNIT,
    dataTestName: 'cert-create-request-org-unit',
    id: 'organizationalUnit',
  },
  {
    label: localizedStrings.CITY_OR_LOCALITY,
    dataTestName: 'cert-create-request-city',
    id: 'cityOrLocality',
    isRequired: true,
  },
  {
    label: localizedStrings.STATE_OR_PROVINCE,
    dataTestName: 'cert-create-request-state',
    id: 'stateOrProvince',
    isRequired: true,
  },
  {
    label: localizedStrings.TWO_LETTER_COUNTRY_CODE,
    dataTestName: 'cert-create-request-country',
    id: 'countryCode',
    isRequired: true,
  },
  {
    label: localizedStrings.SUBJECT_ALTERNATIVE_NAMES,
    dataTestName: 'cert-create-request-alt-names',
    id: 'subjectAlternativeNames',
  },
];

class CreateCertRequestDialog extends Component<Props> {
  isOkEnabled: Function;
  submit: Function;

  constructor() {
    super();
    this.isOkEnabled = this.isOkEnabled.bind(this);
    this.submit = this.submit.bind(this);
  }

  isOkEnabled() {
    return this.props.valid;
  }

  submit(data: Object, props: Props) {
    const certRequest = { ...data };
    certRequest.subjectAlternativeNames = normalizeSANs(
      certRequest.subjectAlternativeNames,
      certRequest.FQDN
    );
    this.props.createCertRequest(certRequest);
  }

  render() {
    const {
      activePanelId,
      handleSubmit,
      intl: { formatMessage },
    } = this.props;
    return (
      <DialogPanel
        activePanelId={activePanelId}
        contentStyle={{ width: '800px', height: null }}
        customButtonText={formatMessage(localizedStrings.CREATE)}
        hideDialogPanel={this.props.hideDialogPanel}
        isOkEnabled={this.isOkEnabled}
        onSave={handleSubmit(this.submit)}
        panelId={CREATE_CERT_REQUEST_PANEL_ID}
        panelTitle={formatMessage(localizedStrings.CERT_CREATE_REQUEST)}
        showCancel
      >
        <form className="portfolio-form-container">
          {fields.map(field => (
            <LabeledField key={field.id} label={formatMessage(field.label)}>
              <Field
                className="input"
                component="input"
                data-test-name={field.dataTestName || ''}
                id={field.id}
                name={field.id}
                type="text"
              />
            </LabeledField>
          ))}
        </form>
      </DialogPanel>
    );
  }
}

function validate(data: Object, props: Props) {
  const errors = {};
  fields.forEach(field => {
    if (field.isRequired && !data[field.id]) {
      errors[field.id] = 'Required';
    }
  });
  return errors;
}

const form = reduxForm({ form: CREATE_CERT_REQUEST_PANEL_ID, validate })(
  CreateCertRequestDialog
);

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({ createCertRequest, hideDialogPanel })
)(injectIntl(form));



// WEBPACK FOOTER //
// ./src/view/Pages/CertsPage/CreateCertRequestDialog.js